import { unreachable } from "../util";
import { RegulationRequestTypeDto } from "../../common/service/server/dto/RegulationRequestTypeDto";
import { DataRequestState } from "../../common/service/server/Types";

export const WAITING_FOR_CONSUMER_STATUS = "Waiting for Consumer";
export const AUTOMATING_TAB_LABEL = "Automating";

export const fmtStateShort = (requestType: RegulationRequestTypeDto | undefined) => {
  if (!requestType?.regulationLaw?.region) {
    // Handle legacy VOLUNTARY behavior when no region data exists
    const abbreviation = requestType?.regulationLaw?.abbreviation;
    if (abbreviation === "VOLUNTARY") {
      return "N/A";
    }
    return "";
  }

  // Always return "N/A" for VOLUNTARY regulation regardless of region
  // Special case for FBAR since they have a law but the requests are voluntary
  if (
    requestType.regulationLaw.slug === "VOLUNTARY" ||
    requestType.regulationLaw.abbreviation === "FDBR"
  ) {
    return "N/A";
  }

  const region = requestType.regulationLaw.region;

  // If regionGroup is EEA_UK, show EU
  if (region.regionGroup === "EEA_UK") {
    return "EU";
  }

  // If regionGroup is CANADA, show CAN
  if (region.regionGroup === "CANADA") {
    return "CAN";
  }

  // If regionCode is US-XX, show just XX
  if (region.regionCode?.startsWith("US-")) {
    return region.regionCode.substring(3);
  }

  // For all other cases, return empty string
  return "";
};

type FormattedStatus =
  | typeof WAITING_FOR_CONSUMER_STATUS
  | "Pending Automation"
  | "Closed"
  | "New"
  | "In Progress";

export const fmtRequestTypeState = (state: DataRequestState): FormattedStatus => {
  switch (state) {
    case "PENDING_EMAIL_VERIFICATION":
      return WAITING_FOR_CONSUMER_STATUS;
    case "AUTOMATING":
      return "Pending Automation";
    case "CLOSED":
      return "Closed";
    case "IDENTITY_VERIFICATION":
    case "NEW":
    case "VERIFY_CONSUMER":
    case "VALIDATE_APPEAL":
      return "New";
    case "CONSUMER_GROUP_SELECTION":
    case "PROCESSING":
    case "DELETE_INFO":
    case "CONTACT_VENDORS":
    case "NOTIFY_CONSUMER":
    case "REMOVE_CONSUMER":
    case "RETRIEVE_DATA":
    case "SEND_DATA":
    case "CORRECT_DATA":
    case "EVALUATE_OBJECTION":
    case "WITHDRAW_CONSENT":
    case "REMOVE_CONSENT":
    case "LIMIT_DATA":
    case "STOP_AUTO_PROFILING":
    case "RESTRICT_PROCESSING":
    case "RESOLVE_APPEAL":
    case "GET_PROFILING_INFO":
      return "In Progress";
    default:
      return unreachable(state);
  }
};

/**
 * Format the specific step of the request instead of grouping like "In Progress." Useful for showing step of a group of
 * requests in the batch context.
 * @param step The current step of a request.
 */
export const fmtLongRequestStep = (step: DataRequestState): string => {
  switch (step) {
    case "PENDING_EMAIL_VERIFICATION":
      return WAITING_FOR_CONSUMER_STATUS;
    case "AUTOMATING":
      return "Pending Automation";
    case "CLOSED":
      return "Closed";
    case "IDENTITY_VERIFICATION":
      return "Identity Verification";
    case "NEW":
      return "New";
    case "VERIFY_CONSUMER":
      return "Verify Consumer";
    case "CONSUMER_GROUP_SELECTION":
      return "Consumer Group Selection";
    case "PROCESSING":
      return "Processing";
    case "DELETE_INFO":
      return "Delete Info";
    case "CONTACT_VENDORS":
      return "Contact Vendors";
    case "NOTIFY_CONSUMER":
      return "Notify Consumer";
    case "REMOVE_CONSUMER":
      return "Remove Consumer";
    case "RETRIEVE_DATA":
      return "Retrieve Data";
    case "SEND_DATA":
      return "Send Data";
    case "CORRECT_DATA":
      return "Correct Data";
    case "EVALUATE_OBJECTION":
      return "Evaluate Objection";
    case "WITHDRAW_CONSENT":
      return "Withdraw Consent";
    case "REMOVE_CONSENT":
      return "Remove Consent";
    case "LIMIT_DATA":
      return "Limit Data";
    case "STOP_AUTO_PROFILING":
      return "Stop Auto-Profiling";
    case "RESTRICT_PROCESSING":
      return "Restrict Processing";
    case "VALIDATE_APPEAL":
      return "Validate Appeal";
    case "RESOLVE_APPEAL":
      return "Resolve Appeal";
    case "GET_PROFILING_INFO":
      return "Get Profiling Info";
    default:
      return unreachable(step);
  }
};
